import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import api, { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const Footer = () => {
  const { t, language } = useLanguage();
  const [settings, setSettings] = useState({
    logo_url: '',
    logo_visible: true,
    site_name: 'BMS',
    site_name_full: 'BrightMind Software',
    contact_email: '',
    contact_phone: '',
    contact_address_tr: '',
    contact_address_en: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const logoUrl = settings.logo_url ? 
    (settings.logo_url.startsWith('http') ? settings.logo_url : `${API_URL}${settings.logo_url}`) : 
    null;

  return (
    <footer data-testid="footer-section" className="bg-bms-light border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              {settings.logo_visible && (
                <>
                  {logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt={settings.site_name}
                      className="h-10 w-auto object-contain"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-bms-primary to-bms-primary-light flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                  )}
                </>
              )}
              <span className="text-xl font-bold text-bms-text">{settings.site_name_full || 'BrightMind Software'}</span>
            </Link>
            <p className="text-bms-primary font-medium text-sm mb-2 italic">
              "The Next Level of Intelligence"
            </p>
            <p className="text-bms-text-light text-sm leading-relaxed max-w-md">
              Kurumsal yazılım, yapay zeka ve ölçeklenebilir dijital sistemler konusunda uzman çözüm ortağınız.
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-bms-text mb-4">Hızlı Linkler</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/hizmetler" className="text-bms-text-light hover:text-bms-primary transition-colors text-sm">
                {t.nav.services}
              </Link>
              <Link to="/projeler" className="text-bms-text-light hover:text-bms-primary transition-colors text-sm">
                {t.nav.projects}
              </Link>
              <Link to="/hakkimizda" className="text-bms-text-light hover:text-bms-primary transition-colors text-sm">
                {t.nav.about}
              </Link>
              <Link to="/iletisim" className="text-bms-text-light hover:text-bms-primary transition-colors text-sm">
                {t.nav.contact}
              </Link>
            </nav>
          </div>
          
          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-bms-text mb-4">İletişim</h4>
            <div className="flex flex-col gap-3">
              {settings.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-2 text-bms-text-light hover:text-bms-primary transition-colors text-sm">
                  <Mail className="w-4 h-4" />
                  {settings.contact_email}
                </a>
              )}
              {settings.contact_phone && (
                <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-2 text-bms-text-light hover:text-bms-primary transition-colors text-sm">
                  <Phone className="w-4 h-4" />
                  {settings.contact_phone}
                </a>
              )}
              {(settings.contact_address_tr || settings.contact_address_en) && (
                <span className="flex items-start gap-2 text-bms-text-light text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {language === 'tr' ? settings.contact_address_tr : settings.contact_address_en}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-bms-text-muted text-sm text-center">
            © 2024 {settings.site_name_full || 'BrightMind Software'}. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
